import {Tr} from "@/i18n/i18n";
import {Keys} from "@/constants/key";
import {BasePanel} from "@/scenes.ui/base-panel";
import {CreatureStatPanel} from "@/scenes.ui/creature-stat-panel";
import {injectable} from "inversify";
import {ImageButton} from "@/scenes.ui/image-button";
import TextUtils from "@/utils/text-utils";
import {Fonts} from "@/constants/font";
import OnceCaller from "@/utils/once-caller";
import {ArtifactSlot, UnlockState} from "@/scenes.ui/artifact-slot";
import {Creature} from "@/store/level/types";
import {CreaturesNavPanel} from "@/scenes.ui/creatures-nav-panel";
import SoundUtils from "@/utils/sound-utils";
import {CreatureUtils} from "@/utils/creature-utils";
import PlayerStore from "@/store/player";
import {BottomPanel} from "@/scenes.ui/bottom-panel";
import SpriteUtils from "@/utils/sprite-utils";
import CU from "@/utils/c-u";
import {ForceSlot} from "@/scenes.ui/force-slot";
import {CreatureTabName} from "@/services/player-creature-service";
import Scene = Phaser.Scene;
import Container = Phaser.GameObjects.Container;
import Sprite = Phaser.GameObjects.Sprite;
import Image = Phaser.GameObjects.Image;
import {BottomForcesPanel} from "@/scenes.ui/bottom-forces-panel";

@injectable()
export abstract class AbstractCreatureService {

    protected SLOT_SIZE_X: number = 130;
    protected SLOT_SIZE_Y: number = 114;
    protected SLOT_SIZE_INNER: number = 110;

    protected FORCE_SLOT_SIZE_X: number = 158;
    protected FORCE_SLOT_SIZE_Y: number = 128;//139;
    protected FORCE_SLOT_SIZE_INNER: number = 134;

    protected creatureHeight!: number;
    protected centralContainer: Container | undefined;

    protected scene: Scene | undefined;
    protected canvas: HTMLCanvasElement | undefined;

    protected creatureStatPanel!: CreatureStatPanel;

    protected backButton!: ImageButton;

    protected bottomPanel: BottomPanel | undefined;

    protected backCallback: (() => unknown) | undefined;

    protected creatureNavPanelY!: number;

    protected characterSprite: Sprite | undefined;

    protected statHolder!: Container;

    protected navPanel: CreaturesNavPanel | undefined;

    protected artifactSlots: ArtifactSlot[] = [];
    protected forceSlots: ForceSlot[] = [];
    protected mainForce: {sprite:Image, bg:Image} | undefined;
    protected forceUpgradabilityInfo: {bg:Image, text:Phaser.GameObjects.Text} | undefined;

    protected creatureIndex: number | undefined;

    protected selectSound!: Phaser.Sound.BaseSound | undefined;
    protected buildingSound!: Phaser.Sound.BaseSound | undefined;
    protected clickSound!: Phaser.Sound.BaseSound | undefined;
    protected completedSound!: Phaser.Sound.BaseSound | undefined;
    protected levelUpSound!: Phaser.Sound.BaseSound | undefined;
    protected awardSound!: Phaser.Sound.BaseSound | undefined;

    protected tabName = CreatureTabName.ARTIFACTS;
    protected tabButtons: Sprite[] = [];
    protected tabs: {tab:CreatureTabName, sprite:Sprite, label: Phaser.GameObjects.Text}[] = [];

    protected initPage(scene: Scene, canvas: HTMLCanvasElement, creatureIndex: number, backCallback: () => unknown) {
        this.scene = scene;
        this.canvas = canvas;
        this.backCallback = backCallback;
        this.creatureIndex = creatureIndex;

        this.selectSound = SoundUtils.addSound(scene, Keys.SOUND_SELECT);
        this.buildingSound = SoundUtils.addSound(scene, Keys.SOUND_BUILDING);
        this.clickSound = SoundUtils.addSound(scene, Keys.SOUND_BUTTON_CLICK);
        this.completedSound = SoundUtils.addSound(scene, Keys.SOUND_COMPLETED);
        this.levelUpSound = SoundUtils.addSound(scene, Keys.SOUND_LEVEL_UP);
        this.awardSound = SoundUtils.addSound(scene, Keys.SOUND_AWARD);

        this.centralContainer = new Container(scene);
        this.centralContainer.setDepth(1100);

        /*
         * Height = 900 => scale 0.7
         * Height = 1400 => scale 1
         */
        let centralScale = 0.75 + (Math.min(1400, canvas.height / CU.S) - 900) / 500 * 0.25;
        // console.log('centralScale = ' + centralScale);
        // let centralScale = 1;
        this.SLOT_SIZE_X = 130 - (Math.min(1400, canvas.height / CU.S) - 900) / 500 * 10;

        let c = 700 * centralScale;
        let a = this.SLOT_SIZE_Y * centralScale;

        let k1 = 1;
        let k2 = 1;
        let k3 = 5;

        let R = 1040;
        let botH = 70;
        let navH = 205;

        // Все расчеты непонятных формул здесь:
        // https://hitmakers.atlassian.net/wiki/spaces/AOF/pages/286752769/Creature+Scene
        //
        let d = (canvas.height / CU.S - navH - c - a - botH) / (k1 + k2 + k3);

        this.creatureHeight = c;
        // console.log('canvas.height = ' + canvas.height);
        // console.log('canvas.width = ' + canvas.width);
        // console.log('d = ' + d);

        let shift = R - c - navH - (k1 + k2) * d;

        // console.log('shift = ' + shift);

        SpriteUtils.addSprite(this.scene!, 0, -shift, Keys.PROFILE_BACK).setOrigin(0, 0).setInteractive().on('pointerup', function() {
            BasePanel.closeAll();
        });

        this.creatureStatPanel = new CreatureStatPanel(this.scene);
        scene.add.existing(this.creatureStatPanel);

        this.backButton = new ImageButton(this.scene!, CU.ETALON_CANVAS_WIDTH - 48, canvas.height / CU.S - 50, Keys.ATLAS_PROFILE, Keys.UI_MENU_ROAD).setVisible(true);
        let backButtonText = TextUtils.addTextByFamily(scene, Tr('k.doroge').toUpperCase(), -2, 40,
            '#f6f6f6', 15, 'normal', 130, Fonts.MYRIADPRO_BOLD);
        this.backButton.add(backButtonText);

        this.bottomPanel = new BottomPanel(scene, 0, canvas.height, PlayerStore.getPlayer()!);
        scene.add.existing(this.bottomPanel);

        SpriteUtils.scale(this.centralContainer, centralScale);
        this.centralContainer.setPosition(canvas.width / 2, (navH + (k1 + k2) * d) * CU.S);
        scene.add.existing(this.centralContainer);

        this.scene!.add.existing(this.backButton);
        this.backButton.setDepth(1101);
        let self = this;
        this.backButton.setCallback(() => {
            OnceCaller.doOnce("closeCrScene", () => {
                BasePanel.closeAll();
                self.tabName = CreatureTabName.ARTIFACTS;
                self.backCallback!();
            });
        });

        const statBack = SpriteUtils.addImage(this.scene, 0, 0, Keys.ATLAS_UI, Keys.UI_STAT_WINDOW_BACK).setOrigin(0,0);

        this.creatureNavPanelY = k1 * (canvas.height - navH - c - a - botH) / (k1 + k2 + k3);

    }

    protected getSlotPositionX(slotNum: number): number {
        let shiftX = - this.SLOT_SIZE_X * 2;
        if (slotNum < 6) {
            return shiftX;
        } else if (slotNum < 11) {
            return shiftX + (slotNum - 6) * this.SLOT_SIZE_X;
        } else {
            return shiftX + this.SLOT_SIZE_X * 4;
        }
    }

    protected getSlotPositionY(slotNum: number): number {
        let shiftY = this.SLOT_SIZE_Y * 0.55;
        if (slotNum < 7) {
            return shiftY + slotNum * this.SLOT_SIZE_Y;
        } else if (slotNum < 10) {
            return shiftY + this.SLOT_SIZE_Y * 6;
        } else {
            return shiftY + this.SLOT_SIZE_Y * 6 - (slotNum - 10) * this.SLOT_SIZE_Y;
        }
    }

    protected getForcesSlotPositionX(slotNum: number): number {
        if (slotNum < 5) {
            return - this.SLOT_SIZE_X * 1.9;
        } else if (slotNum < 10) {
            return this.SLOT_SIZE_X * 1.9;
        } else {
            return (slotNum === 11) ? - this.SLOT_SIZE_X * 1.9 : this.SLOT_SIZE_X * 1.9;
        }
    }

    public static getForceSlotsBaseY(){
        return 102 - 20;
    }

    protected getForceSlotPositionY(slotNum: number): number {
        let shiftY = AbstractCreatureService.getForceSlotsBaseY() / CU.S ;
        if (slotNum < 5) {
            return shiftY + slotNum * this.FORCE_SLOT_SIZE_Y;
        } else if (slotNum < 10){
            return shiftY + (9 - slotNum) * this.FORCE_SLOT_SIZE_Y;
        }else if (slotNum === 10) {
            return shiftY + 5 * this.FORCE_SLOT_SIZE_Y;
        } else if (slotNum === 11) {
            return shiftY + 5 * this.FORCE_SLOT_SIZE_Y;
        } else {
            return shiftY - this.FORCE_SLOT_SIZE_Y;
        }
    }

    protected getMainForceSlotPositionY(slotNum: number): number {
            let shiftY = AbstractCreatureService.getForceSlotsBaseY() / CU.S;
            return (shiftY + 5 * this.FORCE_SLOT_SIZE_Y - 10) / CU.S ;
    }

    protected static getUnlockState(slotsCount: number, artifactsCount: number, slotNum: number): UnlockState {
        if (slotNum < slotsCount) {
            return UnlockState.UNLOCKED;
        } else if (slotNum == slotsCount && artifactsCount >= slotsCount) {
            return UnlockState.CAN_UNLOCK;
        } else {
            return UnlockState.LOCKED;
        }
    }

    protected makeCharacterImage(creature: Creature) {
        const creatureImage: string = Keys.profileCreature(creature.imageId);

        if (this.characterSprite) {
            this.characterSprite.destroy();
            this.characterSprite = undefined;
        }
        if (this.centralContainer) {
            //this.characterSprite = SpriteUtils.addSprite(this.scene!, 0, this.SLOT_SIZE_Y * 5.9, creatureImage).setOrigin(0.5, 1);
            this.characterSprite = SpriteUtils.addSprite(this.scene!, 0, this.SLOT_SIZE_Y - 200, creatureImage, undefined, 1 / CU.S, 1 / CU.S).setOrigin(0.5, 0);
            this.centralContainer!.add(this.characterSprite);
        }
    }

    protected prepareCreatureStats(creature: Creature) {

        let self = this;

        if (this.statHolder) { this.statHolder.destroy(); }
        this.statHolder = new Container(this.scene!, 0,0);
        this.scene!.add.existing(this.statHolder);

        let name = TextUtils.addTextAdvanced(this.scene!, creature.name.toUpperCase(), 300, 20, '#eec761',16,
            'normal', 300, 'center', 0, Fonts.BENGALY_NORMAL);

        let type = TextUtils.addTextAdvanced(this.scene!, Tr('klass'), 0, name.y / CU.S + 20, '#B1B0B0',12,
            'normal', 150, 'center', 0, Fonts.MYRIADPRO_REGULAR).setOrigin(0, 0.5);
        let typeValue = TextUtils.addTextAdvanced(this.scene!, CreatureUtils.getClassName(creature.warriorClass),
            0, type.y / CU.S, '#9cdb57',12,
            'normal', 150, 'center', 0, Fonts.MYRIADPRO_REGULAR).setOrigin(0, 0.5);
        let statIcon = SpriteUtils.addImage(this.scene!, 0, typeValue.y / CU.S + 8, Keys.ATLAS_UI, Keys.UI_STAT_MINE_CIRCLE).setOrigin(0.8);

        type.x = this.canvas!.width / 2 - (type.width + typeValue.width) / 2 - 2 * CU.S;
        typeValue.x = type.getTopRight().x! + 5 * CU.S;
        statIcon.x = typeValue.getTopRight().x! + 25 * CU.S;

        statIcon.setInteractive().on('pointerup', function(pointer: any) {
            self.creatureStatPanel.show(creature, pointer.x, pointer.y + 130 * CU.S,true);
        });

        this.scene!.input.on('pointerdown', () => {
            if (this.creatureStatPanel.visible) {
                this.creatureStatPanel.destroyWindowCompletely();
            }
        });

        this.statHolder.add([name, type, typeValue, statIcon]);
    }

    protected destroyNavPanel() {
        if (this.navPanel) {
            this.navPanel.destroy();
            this.navPanel = undefined;
        }
    }

    protected destroySLots() {
        for (let slot of this.artifactSlots) {
            slot.setVisible(false);
            slot.destroy();
        }
        this.artifactSlots = [];

        for (let slot of this.forceSlots) {
            slot.setVisible(false);
            slot.destroy();
        }
        this.forceSlots = [];

        if (this.mainForce) {
            this.mainForce.sprite.destroy();
            this.mainForce.bg.destroy();
            this.mainForce = undefined;
        }

        if(this.forceUpgradabilityInfo){
            this.forceUpgradabilityInfo.bg.destroy();
            this.forceUpgradabilityInfo.text.destroy();
            this.forceUpgradabilityInfo = undefined;
        }
    }

    protected abstract setupCreaturesPanel(): any;


}

